import React, { useEffect, useState } from "react";
import { Text } from "@fluentui/react-components";
import SpreetailHeader from "../layout/SpreetailHeader";
import { useFilterStyles } from "../styles/filterStyles";
import SpreetailLoader from "../common/SpreetailLoader";
import { handleTokenExpiration } from "../../../shared/authUtils";
import { config } from "../../../shared/config";
import { fetchMarketWatchData } from "../../../shared/apiService";
import { loadMarketWatchFilters } from "../../../shared/marketWatchFilterState";
import { MARKET_WATCH_SHEET } from "../Market-Watch/constants";
import { createDataSheet, generateTimestamp } from "../Market-Watch/excelHelpers";

const API_BASE_URL = config.apiBaseUrl;

interface SheetConfig {
  sheet_name: string;
  tab_color: string;
  freeze_panes: string;
  auto_filter: boolean;
  show_grid_lines: boolean;
  zoom: number;
}

interface Sheet {
  sheet_id: string;
  sheet_type: string;
  sheet_config: SheetConfig;
}

interface ConditionalFormat {
  type: string;
  color?: string;
  negative_color?: string;
  rules?: Array<{
    condition: string;
    value: string;
    format: {
      background: string;
      color: string;
      bold?: boolean;
    };
  }>;
}

interface CalculatedColumn {
  column_id: string;
  header: string;
  data_type: string;
  format: string;
  width: number;
  is_computed: boolean;
  formula: string;
  formula_type: string;
  description: string;
  order: number;
  conditional_format?: ConditionalFormat;
}

interface AggregateRow {
  type: string;
  label: string;
  position: string;
  columns: Record<string, string>;
  format: {
    bold: boolean;
    background: string;
    border_top: string;
  };
}

interface ConditionalFormatRule {
  name: string;
  applies_to: string;
  type: string;
  scale?: {
    min: { value: number; color: string };
    mid: { value: number; color: string };
    max: { value: number; color: string };
  };
  icon_set?: string;
  rules?: Array<{
    condition: string;
    value: number;
    icon: string;
  }>;
}

interface Template {
  _id: string;
  template_name: string;
  template_code: string;
  version: string;
  description: string;
  dashboard_type: string;
  auth0_user_id: string;
  system_template: boolean;
  sheet: Sheet;
  calculated_columns: CalculatedColumn[];
  aggregate_rows: AggregateRow[];
  conditional_formats: ConditionalFormatRule[];
}

// Helper function to get token (matching your mockData.ts pattern)
async function getIdToken(): Promise<string> {
  const token = await OfficeRuntime.storage.getItem("token");
  return token;
}

/**
 * Check if a formula uses structured references.
 * Matches both bracketed [@[column_name]] and un-bracketed [@column_name] forms.
 */
function hasStructuredReferences(formula: string): boolean {
  if (!formula) return false;
  // Bracketed: [@[column_name]] or un-bracketed: [@column_name]
  return /\[@\[([^\]]+)\]\]/.test(formula) || /\[@([A-Za-z_]\w*)\]/.test(formula);
}

/**
 * Normalize structured references from un-bracketed [@column_name] to bracketed [@[column_name]].
 * Office.js reliably handles the bracketed form for setting formulas programmatically.
 */
function normalizeStructuredReferences(formula: string): string {
  if (!formula) return formula;
  // Replace [@column_name] with [@[column_name]] (but skip already-bracketed [@[...]])
  return formula.replace(/\[@(?!\[)([A-Za-z_]\w*)\]/g, "[@[$1]]");
}

export const TemplatesList: React.FC = () => {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>("");
  const [validationError, setValidationError] = useState<string | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
  const [applying, setApplying] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const filterStyles = useFilterStyles();

  useEffect(() => {
    fetchTemplates();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!dropdownOpen) return undefined;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest(".template-dropdown-container")) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownOpen]);

  const fetchTemplates = async () => {
    try {
      const token = await getIdToken();

      if (!token) {
        throw new Error("Token is not configured. Please login again.");
      }

      const url = `${API_BASE_URL}/templates`;

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      console.log("Templates List response", response);

      // Handle 401 Unauthorized
      if (response.status === 401) {
        await handleTokenExpiration("Token expired during templates fetch");
        throw new Error("Token is not configured. Please login again.");
      }

      if (!response.ok) {
        throw new Error(`Failed to fetch templates: ${response.statusText}`);
      }

      const data = await response.json();
      setTemplates(data);
    } catch (err: any) {
      setError(err.message);
      console.error("Error fetching templates:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleLoadTemplate = async () => {
    // Validation: check if template is selected
    if (!selectedTemplateId) {
      setValidationError("Please select a template from the dropdown before loading.");
      return;
    }

    // Clear any previous validation errors and success message
    setValidationError(null);
    setSuccessMessage(null);

    const template = templates.find((t) => t._id === selectedTemplateId);
    if (!template) {
      setValidationError("Selected template not found. Please try selecting again.");
      return;
    }

    setApplying(true);
    try {
      await applyTemplate(template);
      setSuccessMessage(`Template "${template.template_name}" loaded successfully.`);
      setTimeout(() => setSuccessMessage(null), 4000);
    } catch {
      setValidationError("Failed to apply template. Please try again.");
    } finally {
      setApplying(false);
    }
  };

  const applyTemplate = async (template: Template) => {
    try {
      await Excel.run(async (context) => {
        const worksheets = context.workbook.worksheets;
        worksheets.load("items/name");
        await context.sync();

        // Special handling for Advertising: append calculated columns to existing Advertising Raw Data sheet
        if (template.template_code === "advertising") {
          let advertisingSheet = worksheets.items.find((s) => s.name === "Advertising Raw Data");

          if (!advertisingSheet) {
            // No existing advertising data sheet; create it first using existing function
            console.log("📝 No Advertising Raw Data sheet found, creating it first...");

            // Import and call the existing function
            const { handleAdvertisingRawData } = await import("../../../commands/commands");
            await handleAdvertisingRawData(context);

            // Reload worksheets to get the newly created sheet
            worksheets.load("items/name");
            await context.sync();

            // Find the newly created sheet
            advertisingSheet = worksheets.items.find((s) => s.name === "Advertising Raw Data");
            if (!advertisingSheet) {
              Office.context.ui.displayDialogAsync(
                `data:text/html,
        <html>
          <body style="font-family: Segoe UI; padding: 20px; text-align: center;">
            <h3 style="color: #DC2626; margin: 0 0 8px;">Failed to Create Sheet</h3>
            <p style="font-size: 12px; color: #666;">Could not create Advertising Raw Data sheet.</p>
            <button onclick="Office.context.ui.messageParent('close')" 
                    style="background: #DC2626; color: white; border: none; padding: 6px 12px; border-radius: 4px; cursor: pointer;">
              Close
            </button>
          </body>
        </html>`,
                { height: 160, width: 320 }
              );
              return;
            }
          }

          // Now proceed with existing template application logic
          advertisingSheet.activate();
          await context.sync();

          // Utility: convert 1-based column index to Excel letter(s)
          const toColLetter = (n: number) => {
            let num = Math.max(1, Math.floor(n));
            let name = "";
            while (num > 0) {
              const rem = (num - 1) % 26;
              name = String.fromCharCode(65 + rem) + name;
              num = Math.floor((num - 1) / 26);
            }
            return name;
          };

          // Convert Excel column letter(s) to 1-based index
          const colToIndex = (letters: string) => {
            return letters.split("").reduce((acc, ch) => acc * 26 + (ch.charCodeAt(0) - 64), 0);
          };

          const headerRow = 4;
          const dataStartRow = headerRow + 1;

          // Get the used range to determine last row
          const used = advertisingSheet.getUsedRange();
          used.load(["address"]);
          await context.sync();

          // Parse last row from used range address
          const usedAddr = used.address; // e.g., 'Advertising Raw Data'!A1:BJ200
          const usedMatch = usedAddr.match(/!([A-Z]+)(\d+):([A-Z]+)(\d+)$/);
          let lastRowNumber = dataStartRow; // fallback
          if (usedMatch) {
            lastRowNumber = parseInt(usedMatch[4], 10);
          }

          // **KEY FIX: Scan header row to find the TRUE last column with data**
          const headerScanRange = advertisingSheet.getRange(`A${headerRow}:ZZ${headerRow}`);
          headerScanRange.load(["values"]);
          await context.sync();

          const headerVals: any[][] = headerScanRange.values || [[]];
          let actualLastColIndex = 0;

          // Find the rightmost non-empty header by scanning backwards
          if (headerVals[0] && headerVals[0].length) {
            for (let c = headerVals[0].length - 1; c >= 0; c--) {
              const cellVal = headerVals[0][c];
              if (cellVal !== null && cellVal !== undefined && String(cellVal).trim() !== "") {
                actualLastColIndex = c + 1; // Convert to 1-based
                break;
              }
            }
          }

          const actualLastColLetter = toColLetter(actualLastColIndex);
          console.log(
            `🔍 Actual last column with data: index ${actualLastColIndex} (${actualLastColLetter})`
          );
          console.log(`📊 Last row with data: ${lastRowNumber}`);

          // Build a table over the existing data if none exists
          const tables = advertisingSheet.tables;
          tables.load("items/name");
          await context.sync();

          let table = tables.items[0];
          if (!table) {
            const tableRange = advertisingSheet.getRange(
              `A${headerRow}:${actualLastColLetter}${lastRowNumber}`
            );
            table = advertisingSheet.tables.add(tableRange, true);
            table.name = `AdvertisingDataTable`;
            table.style = "TableStyleMedium2";
            await context.sync();
            console.log(
              `✅ Created new table: A${headerRow}:${actualLastColLetter}${lastRowNumber}`
            );
          } else {
            // If table exists but doesn't cover all columns, resize it
            const tableRangeObj = table.getRange();
            tableRangeObj.load(["address"]);
            await context.sync();

            const tableAddr = tableRangeObj.address;
            const tableMatch = tableAddr.match(/!([A-Z]+)(\d+):([A-Z]+)(\d+)$/);
            let tableLastColLetter = actualLastColLetter;

            if (tableMatch) {
              tableLastColLetter = tableMatch[3];
              const tableLastColIndex = colToIndex(tableLastColLetter);

              // If actual data extends beyond table, resize table to cover all data
              if (actualLastColIndex > tableLastColIndex) {
                const newTableRange = advertisingSheet.getRange(
                  `A${headerRow}:${actualLastColLetter}${lastRowNumber}`
                );
                table.resize(newTableRange);
                await context.sync();
                console.log(
                  `📏 Resized table to include all data columns: A${headerRow}:${actualLastColLetter}${lastRowNumber}`
                );
              }
            }
          }

          // **Load actual column names from table FIRST - needed for checking existing columns**
          let tableColumnsInitial = table.columns;
          tableColumnsInitial.load("items/name");
          await context.sync();
          const actualColumnNames = tableColumnsInitial.items.map((c) => c.name);
          console.log(
            `📋 CURRENT TABLE COLUMNS (${actualColumnNames.length} total):`,
            actualColumnNames
          );

          // Sort calculated columns and append them as new columns to the right
          const sortedColumns = (template.calculated_columns || [])
            .slice()
            .sort((a, b) => a.order - b.order);
          if (sortedColumns.length === 0) {
            console.log("No calculated columns to apply.");
            return;
          }

          // **NEW: Check if calculated columns already exist and need to be replaced**
          console.log(`🔍 Checking for existing calculated columns...`);
          const existingCalculatedColumns: Map<string, number> = new Map(); // header -> column index
          
          for (const col of sortedColumns) {
            const existingIndex = actualColumnNames.indexOf(col.header);
            if (existingIndex !== -1) {
              existingCalculatedColumns.set(col.header, existingIndex);
              console.log(`   Found existing column: "${col.header}" at index ${existingIndex}`);
            }
          }

          if (existingCalculatedColumns.size > 0) {
            console.log(`⚠️ Found ${existingCalculatedColumns.size} existing calculated columns`);
            console.log(`   Options: (1) Replace existing, (2) Skip existing, (3) Append as new`);
            console.log(`   Decision: Replacing existing calculated columns with template versions`);
          }

          // Separate columns into: existing (to replace) and new (to append)
          const columnsToReplace: CalculatedColumn[] = [];
          const columnsToAppend: CalculatedColumn[] = [];
          
          for (const col of sortedColumns) {
            if (existingCalculatedColumns.has(col.header)) {
              columnsToReplace.push(col);
            } else {
              columnsToAppend.push(col);
            }
          }
          
          console.log(`📊 Column strategy: Replace ${columnsToReplace.length}, Append ${columnsToAppend.length}`);

          // **STEP 1: Replace existing calculated columns**
          if (columnsToReplace.length > 0) {
            console.log(`\n🔄 REPLACING ${columnsToReplace.length} EXISTING COLUMNS`);
            
            // Batch operations: queue all column operations first, then sync once
            for (const col of columnsToReplace) {
              const colIndex = existingCalculatedColumns.get(col.header)!;
              const letter = toColLetter(colIndex + 1); // Convert 0-based to 1-based
              
              console.log(`➡️ Replacing '${col.header}' at ${letter} (index ${colIndex})`);

              // Set column width
              if (col.width) {
                advertisingSheet.getRange(`${letter}:${letter}`).format.columnWidth = col.width;
              }

              // Apply number format to data body
              if (col.format) {
                const dataRange = advertisingSheet.getRange(
                  `${letter}${dataStartRow}:${letter}${lastRowNumber}`
                );
                dataRange.numberFormat = [[col.format]];
              }

              // Apply formula if computed - use table column method for better compatibility
              if (col.is_computed && col.formula) {
                try {
                  console.log(`   🔧 Applying formula: ${col.formula}`);
                  
                  const usesStructuredRefs = hasStructuredReferences(col.formula);
                  
                  if (usesStructuredRefs) {
                    // Validate column names
                    const structuredRegex = /\[@\[([^\]]+)\]\]/g;
                    const referencedColumns: string[] = [];
                    let match;
                    while ((match = structuredRegex.exec(col.formula)) !== null) {
                      referencedColumns.push(match[1]);
                    }
                    
                    const missingColumns = referencedColumns.filter(c => !actualColumnNames.includes(c));
                    
                    if (missingColumns.length > 0) {
                      console.error(`   ❌ Missing columns: [${missingColumns.join(', ')}]`);
                      throw new Error(`Formula references missing columns: ${missingColumns.join(', ')}`);
                    }
                  }
                  
                  // Use table column method - more efficient and works better on Mac
                  // Get column by name for reliability (works even if column order changes)
                  const tableColumn = table.columns.getItem(col.header);
                  const dataBodyRange = tableColumn.getDataBodyRange();
                  
                  // Apply formula to first row only - Excel will propagate structured refs automatically
                  // This avoids payload size issues and works better cross-platform
                  const firstRow = dataBodyRange.getRow(0);
                  firstRow.formulas = [[col.formula]];
                  
                  console.log(`   ✅ Formula queued for table column (will propagate automatically)`);
                } catch (formulaError: any) {
                  console.error(`   ❌ Failed to apply formula:`, formulaError.message);
                }
              }
            }
            
            // Single sync for all column operations to avoid payload size issues
            await context.sync();
            console.log(`✅ All ${columnsToReplace.length} columns replaced`);
            
            // Force recalculation to ensure formulas compute (especially important for Mac)
            try {
              context.workbook.application.calculate(Excel.CalculationType.full);
              await context.sync();
              console.log(`🔄 Forced full recalculation after replacing columns`);
            } catch (calcError) {
              console.warn(`⚠️ Could not force recalculation:`, calcError);
            }
          }

          // **STEP 2: Append new calculated columns**
          if (columnsToAppend.length === 0) {
            console.log("\n✅ All calculated columns replaced. No new columns to append.");
            return;
          }

          console.log(`\n➕ APPENDING ${columnsToAppend.length} NEW COLUMNS`);

          // Start appending AFTER the actual last column with data
          let startColIndex = actualLastColIndex + 1;

          console.log(
            `🧩 Appending ${columnsToAppend.length} new calculated columns starting at column index ${startColIndex} (${toColLetter(startColIndex)})`
          );

          // Calculate the new last column after adding calculated columns
          const newLastColIndex = startColIndex + columnsToAppend.length - 1;
          const newLastColLetter = toColLetter(newLastColIndex);

          // Set headers for new columns
          const headerValues: string[] = columnsToAppend.map((c) => c.header);
          advertisingSheet.getRange(
            `${toColLetter(startColIndex)}${headerRow}:${newLastColLetter}${headerRow}`
          ).values = [headerValues];
          console.log(
            `🪟 Header row ${headerRow}: added headers [${headerValues.join(", ")}] at ${toColLetter(startColIndex)}${headerRow}:${newLastColLetter}${headerRow}`
          );

          // Resize the table to include the new columns
          const newTableRange = advertisingSheet.getRange(
            `A${headerRow}:${newLastColLetter}${lastRowNumber}`
          );
          table.resize(newTableRange);
          await context.sync();
          console.log(`📏 Table resized to: A${headerRow}:${newLastColLetter}${lastRowNumber}`);

          table.load("columns");
          await context.sync();
          console.log(`🔄 Table reloaded after resize`);

          // 🔍 DIAGNOSTIC: List all actual column names in the table
          const tableColumns = table.columns;
          tableColumns.load("items/name");
          await context.sync();
          const updatedColumnNames = tableColumns.items.map((c) => c.name);
          console.log(
            `📋 UPDATED TABLE COLUMNS (${updatedColumnNames.length} total):`,
            updatedColumnNames
          );

          // Apply width, number formats, and formulas for NEW columns only
          // Batch operations to avoid payload size issues
          const appliedSummary: string[] = [];
          
          for (let i = 0; i < columnsToAppend.length; i++) {
            const col = columnsToAppend[i];
            const colIndex = startColIndex + i;
            const letter = toColLetter(colIndex);

            console.log(
              `➡️ Applying new calculated column '${col.header}' at ${letter}${headerRow} (index ${colIndex}) | width=${col.width ?? "default"}, format=${col.format || "n/a"}, computed=${col.is_computed}`
            );

            // Set column width
            if (col.width) {
              advertisingSheet.getRange(`${letter}:${letter}`).format.columnWidth = col.width;
            }

            // Apply number format to data body
            if (col.format) {
              const dataRange = advertisingSheet.getRange(
                `${letter}${dataStartRow}:${letter}${lastRowNumber}`
              );
              dataRange.numberFormat = [[col.format]];
            }

            // Apply formula if computed - use table column method for better performance and Mac compatibility
            if (col.is_computed && col.formula) {
              try {
                console.log(`🔧 Applying formula for '${col.header}': ${col.formula}`);
                
                // Check if formula uses structured references
                const usesStructuredRefs = hasStructuredReferences(col.formula);
                console.log(`   Formula type: ${usesStructuredRefs ? 'Structured references' : 'Cell references'}`);

                if (usesStructuredRefs) {
                  // ✅ STRUCTURED REFERENCES - Validate column names first
                  console.log(`   Validating structured reference formula...`);
                  
                  // Extract column names from formula
                  const structuredRegex = /\[@\[([^\]]+)\]\]/g;
                  const referencedColumns: string[] = [];
                  let match;
                  while ((match = structuredRegex.exec(col.formula)) !== null) {
                    referencedColumns.push(match[1]);
                  }
                  
                  console.log(`   Referenced columns: [${referencedColumns.join(', ')}]`);
                  console.log(`   Available columns (first 10): [${updatedColumnNames.slice(0, 10).join(', ')}...]`);
                  
                  // Check if all referenced columns exist - use updatedColumnNames after table resize
                  const missingColumns = referencedColumns.filter(col => !updatedColumnNames.includes(col));
                  
                  if (missingColumns.length > 0) {
                    console.error(`   ❌ Missing columns: [${missingColumns.join(', ')}]`);
                    console.error(`   💡 Formula references columns that don't exist in the table`);
                    console.error(`   💡 This template may have been saved from a different data structure`);
                    throw new Error(`Formula references missing columns: ${missingColumns.join(', ')}`);
                  }
                } else {
                  console.log(`   ⚠️ WARNING: Formula uses cell references: ${col.formula}`);
                  console.log(`   This may not work correctly across all rows`);
                  console.log(`   💡 Recommendation: Re-save this template to convert to structured references`);
                }
                
                // Use table column method - more efficient and works better on Mac
                // Get the table column by name (after resize, new columns should have headers)
                const tableColumn = table.columns.getItem(col.header);
                const dataBodyRange = tableColumn.getDataBodyRange();
                
                // Apply formula to first row only - Excel will automatically propagate structured refs to all rows
                // This avoids payload size issues and works better cross-platform
                const firstRow = dataBodyRange.getRow(0);
                firstRow.formulas = [[col.formula]];
                
                console.log(`   ✅ Formula queued for table column (will propagate automatically)`);
              } catch (formulaError: any) {
                console.error(
                  `❌ Failed to apply formula for ${col.header}:`,
                  formulaError.message
                );
                console.error(`   Formula was: ${col.formula}`);
                console.error(`   💡 Tip: This usually means the formula references columns that don't exist`);
                console.error(`   💡 Check that the column names in the formula match the actual column names in your table`);
              }
            }

            appliedSummary.push(
              `${col.header} -> ${letter} (formula=${col.is_computed && col.formula ? col.formula : "none"})`
            );
          }
          
          // Single sync for all column operations to avoid payload size issues
          await context.sync();
          console.log(`✅ All ${columnsToAppend.length} new columns applied`);
          
          // Force recalculation to ensure formulas compute (especially important for Mac)
          try {
            context.workbook.application.calculate(Excel.CalculationType.full);
            await context.sync();
            console.log(`🔄 Forced full recalculation`);
          } catch (calcError) {
            console.warn(`⚠️ Could not force recalculation:`, calcError);
          }
          
          // Verify formulas were applied correctly
          if (columnsToAppend.length > 0 && columnsToAppend[0].is_computed && columnsToAppend[0].formula) {
            const firstNewCol = columnsToAppend[0];
            const firstColIndex = startColIndex;
            const firstLetter = toColLetter(firstColIndex);
            try {
              const verifyCell = advertisingSheet.getRange(`${firstLetter}${dataStartRow}`);
              verifyCell.load("formulas, values");
              await context.sync();
              console.log(
                `✅ Verification - Formula: ${verifyCell.formulas[0][0]}, Value: ${verifyCell.values[0][0]}`
              );
            } catch (verifyError) {
              console.warn(`⚠️ Could not verify formula:`, verifyError);
            }
          }

          console.log(
            `✅ Template "${template.template_name}" applied:\n` +
            `   - Replaced: ${columnsToReplace.length} existing columns\n` +
            `   - Appended: ${columnsToAppend.length} new columns\n` +
            `   - Total calculated columns: ${sortedColumns.length}`
          );
          return;
        }

        // Special handling for Market Watch: append calculated columns to existing MW Raw Data sheet
        if (template.template_code === "market_watch") {
          // Step 1: Find an existing MW Raw Data sheet
          // Priority: active sheet if it's an MW Raw Data sheet, otherwise scan for last one
          let mwDataSheet: Excel.Worksheet | undefined;

          const activeSheet = context.workbook.worksheets.getActiveWorksheet();
          activeSheet.load("name");
          await context.sync();

          // Match "MW Raw Data <timestamp>"
          const isMWDataSheet = (name: string) =>
            name.startsWith("MW Raw Data");

          if (isMWDataSheet(activeSheet.name)) {
            mwDataSheet = activeSheet;
            console.log(`Found active MW Raw Data sheet: "${activeSheet.name}"`);
          } else {
            // Scan all sheets for MW Raw Data prefix, use the last one found
            const mwDataSheets = worksheets.items.filter((s) => isMWDataSheet(s.name));
            if (mwDataSheets.length > 0) {
              mwDataSheet = mwDataSheets[mwDataSheets.length - 1];
              console.log(`Found MW Raw Data sheet: "${mwDataSheet.name}"`);
            }
          }

          // Step 2: Auto-create MW Raw Data sheet if none exists
          if (!mwDataSheet) {
            console.log("No MW Raw Data sheet found, attempting auto-create from saved filters...");

            // Load saved filters from OfficeRuntime.storage
            const savedFilters = await loadMarketWatchFilters(MARKET_WATCH_SHEET);
            const hasFilters =
              savedFilters.asin.length > 0 ||
              savedFilters.keyword.length > 0 ||
              savedFilters.brand.length > 0;

            if (!hasFilters) {
              // No saved filters — cannot auto-create
              Office.context.ui.displayDialogAsync(
                `data:text/html,
        <html>
          <body style="font-family: Segoe UI; padding: 20px; text-align: center;">
            <h3 style="color: #DC2626; margin: 0 0 8px;">No Market Watch Data</h3>
            <p style="font-size: 12px; color: #666;">Please run a Market Watch analysis first to create a data sheet, then load the template.</p>
            <button onclick="Office.context.ui.messageParent('close')"
                    style="background: #DC2626; color: white; border: none; padding: 6px 12px; border-radius: 4px; cursor: pointer;">
              Close
            </button>
          </body>
        </html>`,
                { height: 160, width: 320 }
              );
              return;
            }

            // Fetch data using saved filters
            console.log("Fetching MW data with saved filters:", savedFilters);
            try {
              const data = await fetchMarketWatchData(
                savedFilters.asin,
                savedFilters.keyword,
                savedFilters.brand,
                {
                  isExcludeSameBrand: savedFilters.isExcludeSameBrand,
                  excludedBrands: savedFilters.excludedBrands,
                  isExcludeSpreetailAsin: savedFilters.isExcludeSpreetailAsin,
                }
              );

              // Build applied summary for the sheet header
              const parts: string[] = [];
              if (savedFilters.brand.length > 0) parts.push(`Brand: ${savedFilters.brand.join(", ")}`);
              if (savedFilters.asin.length > 0) parts.push(`ASIN: ${savedFilters.asin.join(", ")}`);
              if (savedFilters.keyword.length > 0) parts.push(`Keyword: ${savedFilters.keyword.join(", ")}`);
              const appliedSummary = `Filters: ${parts.join(" | ")}`;

              const timestamp = generateTimestamp();
              const newSheet = await createDataSheet(context, data, appliedSummary, timestamp);

              // Reload worksheets to find the newly created sheet
              worksheets.load("items/name");
              await context.sync();

              mwDataSheet = worksheets.items.find((s) => s.name === newSheet.name);
              if (!mwDataSheet) {
                Office.context.ui.displayDialogAsync(
                  `data:text/html,
        <html>
          <body style="font-family: Segoe UI; padding: 20px; text-align: center;">
            <h3 style="color: #DC2626; margin: 0 0 8px;">Failed to Create Sheet</h3>
            <p style="font-size: 12px; color: #666;">Could not create Market Watch Data sheet.</p>
            <button onclick="Office.context.ui.messageParent('close')"
                    style="background: #DC2626; color: white; border: none; padding: 6px 12px; border-radius: 4px; cursor: pointer;">
              Close
            </button>
          </body>
        </html>`,
                  { height: 160, width: 320 }
                );
                return;
              }
            } catch (fetchError: any) {
              console.error("Failed to fetch Market Watch data:", fetchError);
              Office.context.ui.displayDialogAsync(
                `data:text/html,
        <html>
          <body style="font-family: Segoe UI; padding: 20px; text-align: center;">
            <h3 style="color: #DC2626; margin: 0 0 8px;">Data Fetch Failed</h3>
            <p style="font-size: 12px; color: #666;">${fetchError.message || "Could not fetch Market Watch data."}</p>
            <button onclick="Office.context.ui.messageParent('close')"
                    style="background: #DC2626; color: white; border: none; padding: 6px 12px; border-radius: 4px; cursor: pointer;">
              Close
            </button>
          </body>
        </html>`,
                { height: 160, width: 320 }
              );
              return;
            }
          }

          // Step 3: Activate the MW Raw Data sheet
          mwDataSheet.activate();
          await context.sync();

          // Utility: convert 1-based column index to Excel letter(s)
          const toColLetter = (n: number) => {
            let num = Math.max(1, Math.floor(n));
            let name = "";
            while (num > 0) {
              const rem = (num - 1) % 26;
              name = String.fromCharCode(65 + rem) + name;
              num = Math.floor((num - 1) / 26);
            }
            return name;
          };

          // Convert Excel column letter(s) to 1-based index
          const colToIndex = (letters: string) => {
            return letters.split("").reduce((acc, ch) => acc * 26 + (ch.charCodeAt(0) - 64), 0);
          };

          // MW Raw Data sheet layout: Row 1-2 = title, Row 3 = filters, Row 4 = data period, Row 5 = empty, Row 6 = headers, Row 7+ = data
          const headerRow = 6;
          const dataStartRow = headerRow + 1;

          // Get the used range to determine last row
          const used = mwDataSheet.getUsedRange();
          used.load(["address"]);
          await context.sync();

          // Parse last row from used range address
          const usedAddr = used.address;
          const usedMatch = usedAddr.match(/!([A-Z]+)(\d+):([A-Z]+)(\d+)$/);
          let lastRowNumber = dataStartRow; // fallback
          if (usedMatch) {
            lastRowNumber = parseInt(usedMatch[4], 10);
          }

          // Scan header row to find the TRUE last column with data
          const headerScanRange = mwDataSheet.getRange(`A${headerRow}:ZZ${headerRow}`);
          headerScanRange.load(["values"]);
          await context.sync();

          const headerVals: any[][] = headerScanRange.values || [[]];
          let actualLastColIndex = 0;

          // Find the rightmost non-empty header by scanning backwards
          if (headerVals[0] && headerVals[0].length) {
            for (let c = headerVals[0].length - 1; c >= 0; c--) {
              const cellVal = headerVals[0][c];
              if (cellVal !== null && cellVal !== undefined && String(cellVal).trim() !== "") {
                actualLastColIndex = c + 1; // Convert to 1-based
                break;
              }
            }
          }

          const actualLastColLetter = toColLetter(actualLastColIndex);
          console.log(
            `MW Raw Data: Actual last column with data: index ${actualLastColIndex} (${actualLastColLetter})`
          );
          console.log(`MW Raw Data: Last row with data: ${lastRowNumber}`);

          // Build a table over the existing data if none exists
          const tables = mwDataSheet.tables;
          tables.load("items/name");
          await context.sync();

          let table = tables.items[0];
          if (!table) {
            const tableRange = mwDataSheet.getRange(
              `A${headerRow}:${actualLastColLetter}${lastRowNumber}`
            );
            table = mwDataSheet.tables.add(tableRange, true);
            table.name = `MWData_${Date.now()}`;
            table.style = "TableStyleMedium2";
            await context.sync();
            console.log(
              `Created new MW table: A${headerRow}:${actualLastColLetter}${lastRowNumber}`
            );
          } else {
            // If table exists but doesn't cover all columns, resize it
            const tableRangeObj = table.getRange();
            tableRangeObj.load(["address"]);
            await context.sync();

            const tableAddr = tableRangeObj.address;
            const tableMatch = tableAddr.match(/!([A-Z]+)(\d+):([A-Z]+)(\d+)$/);

            if (tableMatch) {
              const tableLastColLetter = tableMatch[3];
              const tableLastColIndex = colToIndex(tableLastColLetter);

              if (actualLastColIndex > tableLastColIndex) {
                const newTableRange = mwDataSheet.getRange(
                  `A${headerRow}:${actualLastColLetter}${lastRowNumber}`
                );
                table.resize(newTableRange);
                await context.sync();
                console.log(
                  `Resized MW table to: A${headerRow}:${actualLastColLetter}${lastRowNumber}`
                );
              }
            }
          }

          // Load actual column names from table
          let tableColumnsInitial = table.columns;
          tableColumnsInitial.load("items/name");
          await context.sync();
          const actualColumnNames = tableColumnsInitial.items.map((c) => c.name);
          console.log(
            `MW TABLE COLUMNS (${actualColumnNames.length} total):`,
            actualColumnNames
          );

          // Sort calculated columns and append them as new columns to the right
          const sortedColumns = (template.calculated_columns || [])
            .slice()
            .sort((a, b) => a.order - b.order);
          if (sortedColumns.length === 0) {
            console.log("No calculated columns to apply.");
            return;
          }

          // Check if calculated columns already exist and need to be replaced
          console.log(`Checking for existing calculated columns...`);
          const existingCalculatedColumns: Map<string, number> = new Map();

          for (const col of sortedColumns) {
            const existingIndex = actualColumnNames.indexOf(col.header);
            if (existingIndex !== -1) {
              existingCalculatedColumns.set(col.header, existingIndex);
              console.log(`   Found existing column: "${col.header}" at index ${existingIndex}`);
            }
          }

          if (existingCalculatedColumns.size > 0) {
            console.log(`Found ${existingCalculatedColumns.size} existing calculated columns — replacing`);
          }

          // Separate columns into: existing (to replace) and new (to append)
          const columnsToReplace: CalculatedColumn[] = [];
          const columnsToAppend: CalculatedColumn[] = [];

          for (const col of sortedColumns) {
            if (existingCalculatedColumns.has(col.header)) {
              columnsToReplace.push(col);
            } else {
              columnsToAppend.push(col);
            }
          }

          console.log(`Column strategy: Replace ${columnsToReplace.length}, Append ${columnsToAppend.length}`);

          // STEP A: Replace existing calculated columns
          if (columnsToReplace.length > 0) {
            console.log(`REPLACING ${columnsToReplace.length} EXISTING COLUMNS`);

            for (const col of columnsToReplace) {
              const colIndex = existingCalculatedColumns.get(col.header)!;
              const letter = toColLetter(colIndex + 1);

              console.log(`Replacing '${col.header}' at ${letter} (index ${colIndex})`);

              if (col.width) {
                mwDataSheet.getRange(`${letter}:${letter}`).format.columnWidth = col.width;
              }

              if (col.format) {
                const dataRange = mwDataSheet.getRange(
                  `${letter}${dataStartRow}:${letter}${lastRowNumber}`
                );
                dataRange.numberFormat = [[col.format]];
              }

              if (col.is_computed && col.formula) {
                try {
                  // Normalize un-bracketed [@col] to [@[col]] for Office.js compatibility
                  const normalizedFormula = normalizeStructuredReferences(col.formula);
                  console.log(`   Applying formula: ${normalizedFormula}`);

                  const usesStructuredRefs = hasStructuredReferences(normalizedFormula);

                  if (usesStructuredRefs) {
                    const structuredRegex = /\[@\[([^\]]+)\]\]/g;
                    const referencedColumns: string[] = [];
                    let match;
                    while ((match = structuredRegex.exec(normalizedFormula)) !== null) {
                      referencedColumns.push(match[1]);
                    }

                    const missingColumns = referencedColumns.filter(
                      (c) => !actualColumnNames.includes(c)
                    );

                    if (missingColumns.length > 0) {
                      console.error(`   Missing columns: [${missingColumns.join(", ")}]`);
                      throw new Error(
                        `Formula references missing columns: ${missingColumns.join(", ")}`
                      );
                    }
                  }

                  const tableColumn = table.columns.getItem(col.header);
                  const dataBodyRange = tableColumn.getDataBodyRange();
                  const firstRow = dataBodyRange.getRow(0);
                  firstRow.formulas = [[normalizedFormula]];

                  console.log(`   Formula queued for table column`);
                } catch (formulaError: any) {
                  console.error(`   Failed to apply formula:`, formulaError.message);
                }
              }
            }

            await context.sync();
            console.log(`All ${columnsToReplace.length} columns replaced`);

            try {
              context.workbook.application.calculate(Excel.CalculationType.full);
              await context.sync();
              console.log(`Forced full recalculation after replacing columns`);
            } catch (calcError) {
              console.warn(`Could not force recalculation:`, calcError);
            }
          }

          // STEP B: Append new calculated columns
          if (columnsToAppend.length === 0) {
            console.log("All calculated columns replaced. No new columns to append.");
            return;
          }

          console.log(`APPENDING ${columnsToAppend.length} NEW COLUMNS`);

          let startColIndex = actualLastColIndex + 1;

          console.log(
            `Appending ${columnsToAppend.length} new columns starting at index ${startColIndex} (${toColLetter(startColIndex)})`
          );

          const newLastColIndex = startColIndex + columnsToAppend.length - 1;
          const newLastColLetter = toColLetter(newLastColIndex);

          // Set headers for new columns
          const headerValues: string[] = columnsToAppend.map((c) => c.header);
          mwDataSheet.getRange(
            `${toColLetter(startColIndex)}${headerRow}:${newLastColLetter}${headerRow}`
          ).values = [headerValues];

          // Resize the table to include the new columns
          const newTableRange = mwDataSheet.getRange(
            `A${headerRow}:${newLastColLetter}${lastRowNumber}`
          );
          table.resize(newTableRange);
          await context.sync();
          console.log(`Table resized to: A${headerRow}:${newLastColLetter}${lastRowNumber}`);

          table.load("columns");
          await context.sync();

          // Get updated column names after resize
          const tableColumns = table.columns;
          tableColumns.load("items/name");
          await context.sync();
          const updatedColumnNames = tableColumns.items.map((c) => c.name);
          console.log(
            `UPDATED TABLE COLUMNS (${updatedColumnNames.length} total):`,
            updatedColumnNames
          );

          // Apply width, number formats, and formulas for NEW columns
          for (let i = 0; i < columnsToAppend.length; i++) {
            const col = columnsToAppend[i];
            const colIndex = startColIndex + i;
            const letter = toColLetter(colIndex);

            console.log(
              `Applying new column '${col.header}' at ${letter}${headerRow} (index ${colIndex})`
            );

            if (col.width) {
              mwDataSheet.getRange(`${letter}:${letter}`).format.columnWidth = col.width;
            }

            if (col.format) {
              const dataRange = mwDataSheet.getRange(
                `${letter}${dataStartRow}:${letter}${lastRowNumber}`
              );
              dataRange.numberFormat = [[col.format]];
            }

            if (col.is_computed && col.formula) {
              try {
                // Normalize un-bracketed [@col] to [@[col]] for Office.js compatibility
                const normalizedFormula = normalizeStructuredReferences(col.formula);
                console.log(`Applying formula for '${col.header}': ${normalizedFormula}`);

                const usesStructuredRefs = hasStructuredReferences(normalizedFormula);

                if (usesStructuredRefs) {
                  const structuredRegex = /\[@\[([^\]]+)\]\]/g;
                  const referencedColumns: string[] = [];
                  let match;
                  while ((match = structuredRegex.exec(normalizedFormula)) !== null) {
                    referencedColumns.push(match[1]);
                  }

                  const missingColumns = referencedColumns.filter(
                    (c) => !updatedColumnNames.includes(c)
                  );

                  if (missingColumns.length > 0) {
                    console.error(`   Missing columns: [${missingColumns.join(", ")}]`);
                    throw new Error(
                      `Formula references missing columns: ${missingColumns.join(", ")}`
                    );
                  }
                }

                const tableColumn = table.columns.getItem(col.header);
                const dataBodyRange = tableColumn.getDataBodyRange();
                const firstRow = dataBodyRange.getRow(0);
                firstRow.formulas = [[normalizedFormula]];

                console.log(`   Formula queued for table column`);
              } catch (formulaError: any) {
                console.error(
                  `Failed to apply formula for ${col.header}:`,
                  formulaError.message
                );
              }
            }
          }

          // Single sync for all column operations
          await context.sync();
          console.log(`All ${columnsToAppend.length} new columns applied`);

          // Force recalculation
          try {
            context.workbook.application.calculate(Excel.CalculationType.full);
            await context.sync();
            console.log(`Forced full recalculation`);
          } catch (calcError) {
            console.warn(`Could not force recalculation:`, calcError);
          }

          // Verify formulas were applied correctly
          if (
            columnsToAppend.length > 0 &&
            columnsToAppend[0].is_computed &&
            columnsToAppend[0].formula
          ) {
            const firstColIndex = startColIndex;
            const firstLetter = toColLetter(firstColIndex);
            try {
              const verifyCell = mwDataSheet.getRange(`${firstLetter}${dataStartRow}`);
              verifyCell.load("formulas, values");
              await context.sync();
              console.log(
                `Verification - Formula: ${verifyCell.formulas[0][0]}, Value: ${verifyCell.values[0][0]}`
              );
            } catch (verifyError) {
              console.warn(`Could not verify formula:`, verifyError);
            }
          }

          console.log(
            `Template "${template.template_name}" applied to MW Raw Data:\n` +
              `   - Replaced: ${columnsToReplace.length} existing columns\n` +
              `   - Appended: ${columnsToAppend.length} new columns\n` +
              `   - Total calculated columns: ${sortedColumns.length}`
          );
          return;
        }

        // Default behavior for non-Advertising templates: create a new sheet with calculated columns as a table
        const existingSheet = worksheets.items.find((s) => s.name === template.template_name);
        if (existingSheet) {
          existingSheet.delete();
          await context.sync();
        }

        const newSheet = worksheets.add(template.template_name);
        newSheet.activate();
        await context.sync();

        if (template.sheet?.sheet_config?.tab_color) {
          newSheet.tabColor = template.sheet.sheet_config.tab_color;
        }

        if (template.calculated_columns && template.calculated_columns.length > 0) {
          const sortedColumns = [...template.calculated_columns].sort((a, b) => a.order - b.order);
          const headers = sortedColumns.map((col) => col.header);
          const numCols = headers.length;
          const lastCol = String.fromCharCode(64 + numCols);
          const initialRows = 5; // Start with 5 data rows
          const tableRange = newSheet.getRange(`A1:${lastCol}${1 + initialRows}`);
          const table = newSheet.tables.add(tableRange, true);
          table.name = `${template.template_code}_Table`;
          table.getHeaderRowRange().values = [headers];
          table.style = "TableStyleMedium2";
          await context.sync();

          for (let index = 0; index < sortedColumns.length; index++) {
            const col = sortedColumns[index];
            const colLetter = String.fromCharCode(65 + index);
            newSheet.getRange(`${colLetter}:${colLetter}`).format.columnWidth = col.width || 100;
            const tableColumn = table.columns.getItemAt(index);
            if (col.format) {
              const dataBodyRange = tableColumn.getDataBodyRange();
              dataBodyRange.numberFormat = [[col.format]];
            }
            await context.sync();
            
            // Apply formulas - structured references work best in tables
            if (col.is_computed && col.formula) {
              try {
                console.log(`🔧 Applying formula for '${col.header}': ${col.formula}`);
                const usesStructuredRefs = hasStructuredReferences(col.formula);
                console.log(`   Formula type: ${usesStructuredRefs ? 'Structured references' : 'Cell references'}`);
                
                const dataBodyRange = tableColumn.getDataBodyRange();
                const firstRow = dataBodyRange.getRow(0);
                firstRow.formulas = [[col.formula]];
                await context.sync();
                
                console.log(`✅ Formula applied to first row of ${col.header}`);
              } catch (e) {
                console.warn(`Could not apply formula for ${col.header}:`, e);
              }
            }
          }
        }

        await context.sync();
        console.log(`✅ Template "${template.template_name}" loaded as Excel Table`);
      });
    } catch (err: any) {
      console.error("Error applying template:", err);
      throw err;
    }
  };

  if (loading) {
    return (
      <div className={`${filterStyles.filterContainer} h-full min-h-0 flex flex-col`}>
        <SpreetailHeader />
        <div className="flex-1 flex items-center justify-center gap-4 p-6">
          <SpreetailLoader
            title="Loading templates"
            subtitle="Please wait while we fetch your templates"
            size="large"
          />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`${filterStyles.filterContainer} h-full min-h-0 flex flex-col`}>
        <SpreetailHeader />
        <div className={filterStyles.filterHeader}>
          <Text size={400} weight="semibold" style={{ color: "#0F172A" }}>
            Load Template
          </Text>
          <Text
            size={200}
            style={{ color: "#667085", opacity: 0.9, fontSize: "12px" }}
          >
            Select a template to load into your workbook
          </Text>
        </div>
        <div className="px-4 pb-4 flex justify-center">
          <div className="flex items-center gap-2 bg-red-50 border-[1.5px] border-red-200 text-[#7F1D1D] rounded-lg text-xs py-2 px-3">
            ⚠️ {error}
          </div>
        </div>
        <div className="flex justify-center">
          <button
            onClick={fetchTemplates}
            className="bg-blue-600 text-white border-none rounded-[10px] h-11 px-4 text-sm font-medium cursor-pointer hover:bg-blue-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`${filterStyles.filterContainer} h-full min-h-0`}>
      <SpreetailHeader />
      <div className={filterStyles.filterHeader}>
        <Text size={400} weight="semibold" style={{ color: "#0F172A" }}>
          Load Template
        </Text>
        <Text
          size={200}
          style={{ color: "#667085", opacity: 0.9, fontSize: "12px" }}
        >
          Select a template to load into your workbook
        </Text>
      </div>

      <div className={`${filterStyles.filterContent} px-4 pb-4`}>
        {templates.length === 0 ? (
          <div className="text-center text-[#667085] mt-6 p-6 bg-white border border-gray-200 rounded-[10px]">
            <div className="text-4xl mb-2"></div>
            <div className="text-sm font-semibold text-slate-900 mb-1">No templates available</div>
            <div className="text-xs">Check back later for new templates</div>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {/* Template Selector */}
            <div className="template-dropdown-container">
              <label className="block text-[13px] font-semibold text-gray-800 mb-1.5">
                Select Template
              </label>
              <div className="relative">
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="w-full p-2.5 px-3 border border-gray-200 rounded-md bg-white text-[13px] text-left cursor-pointer flex justify-between items-center overflow-hidden"
                  style={{ color: selectedTemplateId ? "#1f2937" : "#9CA3AF" }}
                  title={
                    selectedTemplateId
                      ? templates.find((t) => t._id === selectedTemplateId)?.template_name ||
                        "Select template"
                      : "Select template"
                  }
                >
                  <span className="truncate mr-2">
                    {selectedTemplateId
                      ? `${templates.find((t) => t._id === selectedTemplateId)?.template_name} (v${templates.find((t) => t._id === selectedTemplateId)?.version})`
                      : "Choose a template..."}
                  </span>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    className={`transition-transform duration-200 flex-shrink-0 ${dropdownOpen ? "rotate-180" : "rotate-0"}`}
                  >
                    <path
                      d="M4 6L8 10L12 6"
                      stroke="#6B7280"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>

                {dropdownOpen && (
                  <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-md z-10 mt-1 max-h-48 overflow-y-auto">
                    {templates.length === 0 ? (
                      <div className="p-2.5 px-3 text-[13px] text-gray-500 text-center">
                        No templates available
                      </div>
                    ) : (
                      templates.map((template, index) => (
                        <button
                          key={template._id}
                          onClick={() => {
                            setSelectedTemplateId(template._id);
                            setDropdownOpen(false);
                            // Clear validation error when user selects a template
                            if (validationError) {
                              setValidationError(null);
                            }
                          }}
                          className="w-full p-2.5 px-3 border-none bg-transparent text-[13px] text-gray-800 text-left cursor-pointer hover:bg-gray-50 overflow-hidden"
                          style={{
                            borderBottom:
                              index < templates.length - 1 ? "1px solid #F3F4F6" : "none",
                          }}
                          title={`${template.template_name} (v${template.version})`}
                        >
                          <span className="truncate block">
                            {template.template_name} (v{template.version})
                          </span>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Template Details Card */}
            {selectedTemplateId && templates.find((t) => t._id === selectedTemplateId) && (
              <div className="bg-white/30 backdrop-blur-[10px] border border-white/60 rounded-[14px] shadow-[0_6px_16px_rgba(0,0,0,0.08)] p-4">
                {(() => {
                  const template = templates.find((t) => t._id === selectedTemplateId)!;
                  return (
                    <>
                      <div className="mb-1.5 text-slate-900 font-semibold">
                        {template.template_name}
                      </div>
                      <div className="text-[#667085] text-xs mb-2">{template.description}</div>
                      <div className="flex items-center gap-2 text-[#9AA3AF] text-[11px]">
                        <span> {template.dashboard_type}</span>
                        <span>v{template.version}</span>
                        {template.system_template && <span>🔧 System</span>}
                      </div>
                    </>
                  );
                })()}
              </div>
            )}

            {/* Success Message */}
            {successMessage && (
              <div className="bg-green-100 rounded-md p-3 flex items-start gap-2">
                <div className="w-5 h-5 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-white text-sm font-bold">&#10003;</span>
                </div>
                <p className="text-[13px] text-green-800 m-0 leading-snug">{successMessage}</p>
              </div>
            )}

            {/* Validation Error Message */}
            {validationError && (
              <div className="bg-red-100 rounded-md p-3 flex items-start gap-2">
                <div className="w-5 h-5 bg-red-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-white text-sm font-bold">!</span>
                </div>
                <p className="text-[13px] text-red-800 m-0 leading-snug">{validationError}</p>
              </div>
            )}

            {/* Load Button */}
            <button
              onClick={handleLoadTemplate}
              disabled={applying}
              className={`w-full h-11 text-white border-none rounded-[10px] text-sm font-medium transition-colors duration-200 shadow-[0_2px_4px_rgba(6,142,117,0.3)] flex items-center justify-center gap-2 ${
                applying
                  ? "bg-[#068E75]/70 cursor-not-allowed"
                  : "bg-[#068E75] cursor-pointer hover:bg-[#057A63]"
              }`}
            >
              {applying && (
                <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                </svg>
              )}
              {applying ? "Applying Template..." : "Load Template"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TemplatesList;